import {createContext, use} from 'react';

// The API's global access gate (Auth0Authorization.AccessPri server-side). Auth0 grants it to nobody
// by default, so its absence is how the SPA recognises an account nobody has set up yet.
export const API_ACCESS_PERMISSION = 'access:pri';

// Sees the Reporting section (Auth0Authorization.ReadReports). Gated on the permission rather than
// the role because Auth0 decides which roles carry it; the reporting service enforces it, and the
// SPA only uses it to show the entry and mount the route.
export const READ_REPORTS_PERMISSION = 'read:reports';

// Capability switches (Auth0Authorization.KillSwitches / WriteSwitches), enforced by the PRI API and
// switchboard-api; the SPA only uses them to offer the Capability switches section's actions. The
// section itself is admin-only, like every other settings section.
export const KILL_SWITCHES_PERMISSION = 'kill:switches';
export const WRITE_SWITCHES_PERMISSION = 'write:switches';
// Sees anything comparing analysts with each other (Auth0Authorization.ReadLeadReports): Reporting's
// lead blocks and the Weekly QA per-analyst accuracy. The API enforces it; the SPA only hides what the
// API would refuse.
export const READ_LEAD_REPORTS_PERMISSION = 'read:reports:leads';

export type AuthUser = {
  username: string;
  displayName: string;
  role: string;
  allowedVerticals: string[] | null;
  skipP0Approval: boolean;
  permissions: string[];
};

export type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  error?: Error | null;
  // The stored Auth0 session could not be renewed (e.g. its refresh token outlived the upstream
  // identity provider's session limit). Not an error the user can act on beyond signing in again,
  // so LoginPage shows it as a notice rather than surfacing the SDK's internal message.
  sessionExpired?: boolean;
  login: () => Promise<void>;
  loginWithDevAuth: (email: string, displayName?: string) => Promise<void>;
  logout: () => void;
};

export const AuthContext = createContext<AuthContextType>(null!);

export const useAuth = () => use(AuthContext);
